import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { CadastroCnpjField } from '@/components/ClientesAgrada/CadastroCnpjField'
import { ClientesAgradaPlanoSelect } from '@/components/ClientesAgrada/ClientesAgradaPlanoSelect'
import { FINANCE_C4_STATUS_LABELS } from '@/components/Financeiro/financeC4ContractStatus'

const FINANCE_STATUS = [
  'EM_ANDAMENTO',
  'CONTRATO_GERADO_PDF',
  'CONTRATO_ENVIADO',
  'CONTRATO_ASSINADO',
] as const

export type CadastroFinanceStatus = (typeof FINANCE_STATUS)[number]

export type CadastroClienteForm = {
  nome: string
  cnpj: string
  contatoNome: string
  telefone: string
  responsavelAssinaturaNome: string
  responsavelAssinaturaEmail: string
  responsavelFinanceiroNome: string
  emailFinanceiro: string
  vendedorId: string
  planId: string
  valorMensal: string
  dataFechamento: string
  contractStartDate: string
  firstPaymentDate: string
  contractDeliveryMode: 'pdf' | 'link' | ''
  contractStatus: CadastroFinanceStatus
  contractSignatureLink: string
  endereco: string
  servico: string
  observacoes: string
}

export function emptyCadastroClienteForm(date: string): CadastroClienteForm {
  return {
    nome: '',
    cnpj: '',
    contatoNome: '',
    telefone: '',
    responsavelAssinaturaNome: '',
    responsavelAssinaturaEmail: '',
    responsavelFinanceiroNome: '',
    emailFinanceiro: '',
    vendedorId: '',
    planId: '',
    valorMensal: '',
    dataFechamento: date,
    contractStartDate: date,
    firstPaymentDate: date,
    contractDeliveryMode: 'pdf',
    contractStatus: 'CONTRATO_ASSINADO',
    contractSignatureLink: '',
    endereco: '',
    servico: '',
    observacoes: '',
  }
}

const fieldClass =
  'w-full rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-sm text-text-primary'

type Seller = { id: string; name: string; role: string }

type Props = {
  form: CadastroClienteForm
  sellers: Seller[]
  sellerLabel: (role: string) => string
  today: string
  contractFileName: string
  signedContractField?: ReactNode
  onChange: (patch: Partial<CadastroClienteForm>) => void
  onContractFile: (file: File | null) => void
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className='grid gap-1 text-xs text-text-muted'>
      {label}
      {children}
    </label>
  )
}

function Section({
  title,
  children,
  className,
}: {
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('glass-card grid content-start gap-3', className)}>
      <h3 className='text-[11px] font-bold uppercase tracking-wider text-text-muted'>{title}</h3>
      {children}
    </section>
  )
}

export function ClientesAgradaCadastroFields({
  form,
  sellers,
  sellerLabel,
  today,
  contractFileName,
  signedContractField,
  onChange,
  onContractFile,
}: Props) {
  const set = (key: keyof CadastroClienteForm) => (value: string) => onChange({ [key]: value })

  return (
    <>
      <Section title='Venda' className='xl:col-span-2'>
        <div className='grid gap-3 md:grid-cols-6'>
          <div className='md:col-span-6'>
            <span className='mb-1 block text-xs text-text-muted'>Plano</span>
            <ClientesAgradaPlanoSelect
              planId={form.planId}
              onSelect={(plan) =>
                onChange({
                  planId: plan.id,
                  servico: plan.nome,
                  valorMensal: String(plan.valorMensal),
                  ...(plan.descricao.trim().length >= 10 ? { observacoes: plan.descricao.trim() } : {}),
                })
              }
            />
          </div>
          <div className='md:col-span-2'>
            <Field label='Empresa'>
              <input className={fieldClass} value={form.nome} required onChange={(e) => set('nome')(e.target.value)} />
            </Field>
          </div>
          <div className='md:col-span-2'>
            <Field label='Vendedor'>
              <select className={fieldClass} value={form.vendedorId} required onChange={(e) => set('vendedorId')(e.target.value)}>
                {sellers.length === 0 && <option value=''>Nenhum vendedor ativo</option>}
                {sellers.map((seller) => (
                  <option key={seller.id} value={seller.id}>
                    {seller.name} · {sellerLabel(seller.role)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className='md:col-span-2'>
            <Field label='Serviço contratado'>
              <input className={fieldClass} required value={form.servico} onChange={(e) => set('servico')(e.target.value)} />
            </Field>
          </div>
          <div className='md:col-span-3'>
            <Field label='Valor mensal (R$)'>
              <input className={fieldClass} inputMode='decimal' required value={form.valorMensal} onChange={(e) => set('valorMensal')(e.target.value)} />
            </Field>
          </div>
          <div className='md:col-span-3'>
            <Field label='Data de fechamento'>
              <input className={fieldClass} type='date' required min='2020-01-01' max={today} value={form.dataFechamento} onChange={(e) => set('dataFechamento')(e.target.value)} />
            </Field>
          </div>
        </div>
      </Section>

      <Section title='Assinatura do contrato'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <Field label='Nome de quem assina'>
            <input className={fieldClass} required value={form.responsavelAssinaturaNome} onChange={(e) => set('responsavelAssinaturaNome')(e.target.value)} />
          </Field>
          <Field label='E-mail do assinante'>
            <input className={fieldClass} type='email' required value={form.responsavelAssinaturaEmail} onChange={(e) => set('responsavelAssinaturaEmail')(e.target.value)} />
          </Field>
          <div className='sm:col-span-2'>
            <Field label='CNPJ'>
              <CadastroCnpjField value={form.cnpj} onChange={onChange} />
            </Field>
          </div>
          <div className='sm:col-span-2'>
            <Field label='Endereço'>
              <input className={fieldClass} value={form.endereco} onChange={(e) => set('endereco')(e.target.value)} />
            </Field>
          </div>
        </div>
      </Section>

      <Section title='Contato'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <Field label='Nome'>
            <input className={fieldClass} required value={form.contatoNome} onChange={(e) => set('contatoNome')(e.target.value)} />
          </Field>
          <Field label='WhatsApp'>
            <input className={fieldClass} required value={form.telefone} onChange={(e) => set('telefone')(e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title='Contato financeiro'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <Field label='Nome'>
            <input className={fieldClass} required value={form.responsavelFinanceiroNome} onChange={(e) => set('responsavelFinanceiroNome')(e.target.value)} />
          </Field>
          <Field label='E-mail'>
            <input className={fieldClass} type='email' required value={form.emailFinanceiro} onChange={(e) => set('emailFinanceiro')(e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title='Datas e formato'>
        <div className='grid gap-3 sm:grid-cols-2'>
          <Field label='Data inicial do contrato'>
            <input className={fieldClass} type='date' required value={form.contractStartDate} onChange={(e) => set('contractStartDate')(e.target.value)} />
          </Field>
          <Field label='Data inicial de pagamento'>
            <input className={fieldClass} type='date' required value={form.firstPaymentDate} onChange={(e) => set('firstPaymentDate')(e.target.value)} />
          </Field>
        </div>
        <div className='grid grid-cols-2 gap-2'>
          {(
            [
              ['pdf', 'PDF do contrato'],
              ['link', 'Link do contrato'],
            ] as const
          ).map(([value, label]) => {
            const selected = form.contractDeliveryMode === value
            return (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                  selected
                    ? 'border-accent bg-accent/10 text-text-primary'
                    : 'border-[var(--glass-border)] text-text-muted',
                )}
              >
                <input
                  type='radio'
                  name='formato'
                  checked={selected}
                  onChange={() => onChange({ contractDeliveryMode: value })}
                />
                {label}
              </label>
            )
          })}
        </div>
      </Section>

      <Section title='Situação no financeiro' className='xl:col-span-2'>
        <div className='grid gap-3 lg:grid-cols-2'>
          <Field label='Status do chamado'>
            <select
              className={fieldClass}
              value={form.contractStatus}
              onChange={(e) =>
                onChange({
                  contractStatus: e.target.value as CadastroClienteForm['contractStatus'],
                })
              }
            >
              {FINANCE_STATUS.map((status) => (
                <option key={status} value={status}>
                  {FINANCE_C4_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </Field>
          <Field label='Link para assinatura'>
            <input
              className={fieldClass}
              type='url'
              inputMode='url'
              placeholder='https://...'
              value={form.contractSignatureLink}
              onChange={(e) => set('contractSignatureLink')(e.target.value)}
            />
          </Field>
          {form.contractStatus === 'CONTRATO_ASSINADO' ? (
            signedContractField
          ) : (
            <Field label='PDF do contrato (não assinado)'>
              <input
                className={fieldClass}
                type='file'
                accept='application/pdf,.pdf'
                onChange={(e) => onContractFile(e.target.files?.[0] ?? null)}
              />
              {contractFileName && <span className='text-xs text-text-muted'>{contractFileName}</span>}
            </Field>
          )}
          <Field label='Observações'>
            <textarea
              className={`${fieldClass} min-h-20 resize-y`}
              required
              minLength={10}
              value={form.observacoes}
              placeholder='Escopo, serviços, prazos e condições acordadas'
              onChange={(e) => set('observacoes')(e.target.value)}
            />
          </Field>
        </div>
      </Section>
    </>
  )
}
